import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowUpDown,
  Filter,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  RefreshCw,
  Zap,
  CheckCircle,
  XCircle,
  AlertCircle,
  Sparkles,
  ChevronRight,
  Eye,
  EyeOff,
  BarChart3,
  Target,
  DollarSign
} from 'lucide-react';

interface KeywordData {
  keywordId: string;
  text: string;
  campaignName: string;
  adGroupName: string;
  matchType: string;
  geoScope: string;
  totalScore: number;
  relevanceScore: number;
  intentScore: number;
  perfScore: number;
  volumeScore: number;
  metrics: {
    clicks: number;
    impressions: number;
    costMicros: number;
    conversions: number;
    convValue: number;
    ctr: number;
    cvr: number;
    roas: number;
  };
  intent: string;
}

interface Props {
  data: KeywordData[];
  loading: boolean;
  onRefresh: () => void;
}

const KeywordsAudit: React.FC<Props> = ({ data, loading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof KeywordData>('totalScore');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterScore, setFilterScore] = useState<number | null>(null);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(k => 
        k.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        k.campaignName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        k.adGroupName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterScore !== null) {
      if (filterScore >= 70) {
        filtered = filtered.filter(k => k.totalScore >= 70);
      } else if (filterScore >= 40) {
        filtered = filtered.filter(k => k.totalScore >= 40 && k.totalScore < 70);
      } else {
        filtered = filtered.filter(k => k.totalScore < 40);
      }
    }

    filtered.sort((a, b) => {
      const aVal = sortField.includes('.') 
        ? sortField.split('.').reduce((obj: any, key) => obj[key], a)
        : a[sortField as keyof KeywordData];
      const bVal = sortField.includes('.')
        ? sortField.split('.').reduce((obj: any, key) => obj[key], b)
        : b[sortField as keyof KeywordData];

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [data, searchTerm, sortField, sortDirection, filterScore]);

  const handleSort = (field: keyof KeywordData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getScoreBadge = (score: number) => {
    const getColor = () => {
      if (score >= 70) return 'from-green-500 to-emerald-500';
      if (score >= 40) return 'from-yellow-500 to-amber-500';
      return 'from-red-500 to-rose-500';
    };

    const getIcon = () => {
      if (score >= 70) return <TrendingUp className="w-3 h-3" />;
      if (score >= 40) return <AlertCircle className="w-3 h-3" />;
      return <TrendingDown className="w-3 h-3" />;
    };

    const getLabel = () => {
      if (score >= 70) return 'Promuj';
      if (score >= 40) return 'Testuj';
      return 'Wyklucz';
    };

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r ${getColor()} text-white text-xs font-medium`}>
        {getIcon()}
        <span>{getLabel()}</span>
        <span className="opacity-75">({score})</span>
      </div>
    );
  };

  const exportToCSV = () => {
    const headers = ['Słowo kluczowe', 'Kampania', 'Grupa reklam', 'Wynik', 'CTR', 'CVR', 'Konwersje', 'ROAS', 'Koszt', 'CPA'];
    const rows = processedData.map(k => [
      k.text,
      k.campaignName,
      k.adGroupName,
      k.totalScore,
      (k.metrics.ctr * 100).toFixed(2) + '%',
      (k.metrics.cvr * 100).toFixed(2) + '%',
      k.metrics.conversions,
      k.metrics.roas.toFixed(2),
      (k.metrics.costMicros / 1000000).toFixed(2) + ' PLN',
      k.metrics.conversions > 0 ? ((k.metrics.costMicros / 1000000) / k.metrics.conversions).toFixed(2) + ' PLN' : 'N/A'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keywords-audit-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Zap className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-pulse" />
          <p className="text-white/70">Analizuję słowa kluczowe...</p>
        </motion.div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-4" />
        <p className="text-white/60 mb-4">Brak danych do analizy</p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onRefresh}
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium"
        >
          Uruchom pierwszy audyt
        </motion.button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            Audyt Słów Kluczowych AI
          </h2>
          <p className="text-white/60 mt-1">
            Analiza {processedData.length} słów z oceną ML
          </p>
        </div>
        
        <div className="flex gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRefresh}
            disabled={loading}
            className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium hover:bg-white/20 transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={exportToCSV}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium"
          >
            <Download className="w-4 h-4" />
          </motion.button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Szukaj słowa kluczowego..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-purple-500/50 transition-all"
          />
        </div>
        
        <div className="flex gap-2">
          {[
            { value: 70, label: 'Promuj', color: 'from-green-600 to-emerald-600' },
            { value: 40, label: 'Testuj', color: 'from-yellow-600 to-amber-600' },
            { value: 0, label: 'Wyklucz', color: 'from-red-600 to-rose-600' }
          ].map((filter) => (
            <motion.button
              key={filter.value}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilterScore(filterScore === filter.value ? null : filter.value)}
              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                filterScore === filter.value
                  ? `bg-gradient-to-r ${filter.color} text-white`
                  : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white/70 hover:bg-white/20'
              }`}
            >
              <Filter className="w-4 h-4 inline mr-2" />
              {filter.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Data Table */}
      <div className="overflow-hidden rounded-xl bg-white/5 backdrop-blur-xl border border-white/10">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/60 font-medium">
                  <input
                    type="checkbox"
                    checked={selectedKeywords.size === processedData.length && processedData.length > 0}
                    onChange={() => {
                      if (selectedKeywords.size === processedData.length) {
                        setSelectedKeywords(new Set());
                      } else {
                        setSelectedKeywords(new Set(processedData.map(k => k.keywordId)));
                      }
                    }}
                    className="rounded bg-white/10 border-white/20"
                  />
                </th>
                <th 
                  className="text-left p-4 text-white/60 font-medium cursor-pointer hover:text-white/80 transition-colors"
                  onClick={() => handleSort('text')}
                >
                  <div className="flex items-center gap-2">
                    Słowo kluczowe
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </th>
                <th className="text-left p-4 text-white/60 font-medium">Kampania</th>
                <th 
                  className="text-left p-4 text-white/60 font-medium cursor-pointer hover:text-white/80 transition-colors"
                  onClick={() => handleSort('totalScore')}
                >
                  <div className="flex items-center gap-2">
                    Ocena AI
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </th>
                <th className="text-center p-4 text-white/60 font-medium">Metryki</th>
                <th className="text-right p-4 text-white/60 font-medium">CTR</th>
                <th className="text-right p-4 text-white/60 font-medium">Konwersje</th>
                <th className="text-right p-4 text-white/60 font-medium">ROAS</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {processedData.map((keyword, index) => (
                  <motion.tr
                    key={keyword.keywordId}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.02 }}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedKeywords.has(keyword.keywordId)}
                        onChange={() => {
                          const newSelected = new Set(selectedKeywords);
                          if (newSelected.has(keyword.keywordId)) {
                            newSelected.delete(keyword.keywordId);
                          } else {
                            newSelected.add(keyword.keywordId);
                          }
                          setSelectedKeywords(newSelected);
                        }}
                        className="rounded bg-white/10 border-white/20"
                      />
                    </td>
                    <td className="p-4">
                      <div>
                        <div className="font-medium text-white">{keyword.text}</div>
                        <div className="text-xs text-white/40 mt-1">
                          {keyword.matchType} • {keyword.geoScope === 'local' ? '📍 Lokalny' : '🌍 Krajowy'}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm">
                        <div className="text-white/80">{keyword.campaignName}</div>
                        <div className="text-white/40 text-xs mt-1">{keyword.adGroupName}</div>
                      </div>
                    </td>
                    <td className="p-4">
                      {getScoreBadge(keyword.totalScore)}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1 justify-center">
                        {[
                          { label: 'R', value: keyword.relevanceScore },
                          { label: 'I', value: keyword.intentScore },
                          { label: 'P', value: keyword.perfScore },
                          { label: 'V', value: keyword.volumeScore }
                        ].map((metric) => (
                          <div
                            key={metric.label}
                            className="px-2 py-1 rounded bg-white/10 text-xs text-white/60"
                            title={`${metric.label}: ${metric.value}`}
                          >
                            {metric.label}:{metric.value}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-white/80">{(keyword.metrics.ctr * 100).toFixed(2)}%</span>
                        {keyword.metrics.ctr > 0.02 ? (
                          <TrendingUp className="w-4 h-4 text-green-400" />
                        ) : keyword.metrics.ctr < 0.01 ? (
                          <TrendingDown className="w-4 h-4 text-red-400" />
                        ) : (
                          <Minus className="w-4 h-4 text-white/20" />
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-medium ${
                        keyword.metrics.conversions > 0 ? 'text-green-400' : 'text-white/40'
                      }`}>
                        {keyword.metrics.conversions}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-medium ${
                        keyword.metrics.roas > 1 ? 'text-green-400' : 
                        keyword.metrics.roas > 0.5 ? 'text-yellow-400' : 
                        'text-red-400'
                      }`}>
                        {keyword.metrics.roas.toFixed(2)}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Actions */}
      <AnimatePresence>
        {selectedKeywords.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 p-4 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/20 shadow-2xl"
          >
            <div className="flex items-center gap-4">
              <span className="text-white/70">
                Wybrano: <span className="font-semibold text-white">{selectedKeywords.size}</span> słów
              </span>
              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium text-sm"
                >
                  Promuj
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-yellow-600 to-amber-600 text-white font-medium text-sm"
                >
                  Testuj
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-600 text-white font-medium text-sm"
                >
                  Wyklucz
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KeywordsAudit;
